import React from 'react';
import { X, ShieldCheck, FileText, Lock, Scale, Mail } from 'lucide-react';

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'privacy' | 'terms';
}

export const LegalModal: React.FC<LegalModalProps> = ({ 
  isOpen, 
  onClose, 
  initialTab = 'privacy' 
}) => {
  const [activeTab, setActiveTab] = React.useState<'privacy' | 'terms'>(initialTab);

  React.useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-6 bg-black/85 backdrop-blur-md animate-fade-in"
      onClick={onClose}
    >
      <div 
        className="bg-[#0e0e0e] border border-stone-800 w-full max-w-4xl max-h-[88vh] rounded-3xl shadow-[0_25px_70px_rgba(0,0,0,0.95)] flex flex-col relative overflow-hidden text-stone-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Bar */}
        <div className="p-6 md:px-8 border-b border-stone-800 flex items-center justify-between bg-stone-900/40">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-primary/20 text-primary flex items-center justify-center border border-primary/30">
              {activeTab === 'privacy' ? <ShieldCheck size={20} /> : <Scale size={20} />}
            </div>
            <div>
              <h2 className="font-serif text-xl md:text-2xl font-bold text-white">
                {activeTab === 'privacy' ? 'Política de Privacidade' : 'Termos de Uso'}
              </h2>
              <p className="text-[11px] font-sans text-stone-400">
                Literafan &bull; Atualizado em Setembro de 2026
              </p>
            </div>
          </div>

          <button 
            onClick={onClose}
            className="p-2.5 rounded-full text-stone-400 hover:text-white bg-stone-800/80 hover:bg-stone-700 transition-colors"
            title="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="px-6 md:px-8 pt-4 pb-2 flex gap-3 border-b border-stone-800/60 bg-[#0a0a0a]">
          <button
            onClick={() => setActiveTab('privacy')}
            className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-sans font-bold transition-all ${
              activeTab === 'privacy' 
                ? 'bg-primary text-white shadow-md shadow-primary/20' 
                : 'text-stone-400 hover:text-stone-200 hover:bg-stone-900'
            }`}
          >
            <Lock size={14} />
            <span>Política de Privacidade (LGPD)</span>
          </button>

          <button
            onClick={() => setActiveTab('terms')}
            className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-sans font-bold transition-all ${
              activeTab === 'terms' 
                ? 'bg-primary text-white shadow-md shadow-primary/20' 
                : 'text-stone-400 hover:text-stone-200 hover:bg-stone-900'
            }`}
          >
            <FileText size={14} />
            <span>Termos de Uso & Comunidade</span>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="p-6 md:p-8 overflow-y-auto space-y-6 font-sans text-sm leading-relaxed text-stone-300">
          {activeTab === 'privacy' ? (
            <div className="space-y-6">
              <div className="p-4 bg-primary/10 border border-primary/20 rounded-2xl text-xs text-stone-200">
                <p className="font-semibold text-primary mb-1">Compromisso com a sua Privacidade e Proteção de Dados</p>
                O Literafan valoriza a confiança de seus leitores e está em total conformidade com a Lei Geral de Proteção de Dados Pessoais (LGPD - Lei nº 13.709/2018). Tratamos seus dados pessoais com transparência, segurança e respeito.
              </div>

              <div>
                <h3 className="font-serif text-lg font-bold text-white mb-2 flex items-center gap-2">
                  <span className="text-primary font-sans text-sm font-bold">1.</span> Dados Pessoais que Coletamos
                </h3>
                <p className="mb-2">Para viabilizar a experiência comunitária do site, coletamos as seguintes informações:</p>
                <ul className="list-disc pl-6 space-y-1.5 text-stone-400">
                  <li><strong className="text-stone-200">Inscrição na Newsletter:</strong> endereço de e-mail e, opcionalmente, o seu nome completo ou nome de exibição.</li>
                  <li><strong className="text-stone-200">Conta de Membro e Interações:</strong> ao se cadastrar ou conectar via Google, registramos seu identificador único, nome de exibição e foto pública de perfil.</li>
                  <li><strong className="text-stone-200">Comentários e Curtidas:</strong> armazenamos as mensagens que você voluntariamente publica nos artigos e fanarts, além do registro de artigos curtidos, associados ao seu perfil de membro.</li>
                  <li><strong className="text-stone-200">Dados Técnicos e Cookies:</strong> registros estritamente necessários de sessão, preferências de exibição e segurança da plataforma.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-serif text-lg font-bold text-white mb-2 flex items-center gap-2">
                  <span className="text-primary font-sans text-sm font-bold">2.</span> Finalidade do Tratamento
                </h3>
                <p className="mb-2">Seus dados são utilizados exclusivamente para:</p>
                <ul className="list-disc pl-6 space-y-1.5 text-stone-400">
                  <li>Enviar periodicamente novidades literárias, artigos recomendados, resenhas de livros e atualizações da comunidade Literafan.</li>
                  <li>Habilitar e autenticar a sua permissão para interagir no site (curtir e publicar comentários em artigos e fanarts).</li>
                  <li>Manter a segurança e moderação do mural comunitário, coibindo robôs, perfis automatizados e condutas ofensivas.</li>
                  <li>Aprimorar o conteúdo, a performance e a usabilidade do site para todos os leitores.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-serif text-lg font-bold text-white mb-2 flex items-center gap-2">
                  <span className="text-primary font-sans text-sm font-bold">3.</span> Cookies, Publicidade e Google AdSense
                </h3>
                <p className="mb-2">
                  O Literafan pode utilizar o serviço de anúncios de terceiros <strong className="text-white">Google AdSense</strong>. Informamos que:
                </p>
                <ul className="list-disc pl-6 space-y-1.5 text-stone-400">
                  <li>Fornecedores terceiros, incluindo o Google, utilizam cookies para veicular anúncios com base nas visitas anteriores do usuário ao Literafan ou a outros websites.</li>
                  <li>Com o uso de cookies de publicidade, o Google e seus parceiros podem veicular anúncios com base nas visitas feitas aos seus sites e/ou a outros sites na Internet.</li>
                  <li>Você pode desativar a publicidade personalizada acessando as <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Configurações de Anúncios do Google</a> ou através do portal independente <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">aboutads.info/choices</a>.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-serif text-lg font-bold text-white mb-2 flex items-center gap-2">
                  <span className="text-primary font-sans text-sm font-bold">4.</span> Não Compartilhamento e Segurança
                </h3>
                <p>
                  O Literafan <strong className="text-white">NUNCA vende ou comercializa</strong> seus dados pessoais ou lista de e-mails com terceiros. As informações de login e newsletter permanecem armazenadas em infraestrutura em nuvem segura com criptografia (Google Cloud / Firebase Firestore), respeitando os mais rigorosos padrões de segurança da informação.
                </p>
              </div>

              <div>
                <h3 className="font-serif text-lg font-bold text-white mb-2 flex items-center gap-2">
                  <span className="text-primary font-sans text-sm font-bold">4.</span> Seus Direitos (LGPD)
                </h3>
                <p className="mb-2">Como titular dos seus dados, você possui o direito de:</p>
                <ul className="list-disc pl-6 space-y-1.5 text-stone-400">
                  <li><strong className="text-stone-200">Cancelar a inscrição na Newsletter:</strong> a qualquer instante, por meio do link de descadastro existente no rodapé dos e-mails recebidos ou enviando mensagem para nós.</li>
                  <li><strong className="text-stone-200">Solicitar a exclusão ou retificação:</strong> requerer a exclusão completa dos seus dados cadastrais, curtidas ou comentários dos nossos bancos de dados.</li>
                  <li><strong className="text-stone-200">Confirmar a existência de tratamento:</strong> obter detalhes sobre quais dados seus estão armazenados.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-serif text-lg font-bold text-white mb-2 flex items-center gap-2">
                  <span className="text-primary font-sans text-sm font-bold">5.</span> Armazenamento e Segurança
                </h3>
                <p>
                  Adotamos salvaguardas técnicas, administrativas e organizacionais para proteger seus dados contra acessos não autorizados, perdas ou alterações ilícitas. Os dados da newsletter e contas de membros são mantidos enquanto a sua inscrição estiver ativa ou até a solicitação de revogação.
                </p>
              </div>

              <div className="p-4 bg-stone-900/60 border border-stone-800 rounded-2xl flex items-center gap-3 text-xs text-stone-400">
                <Mail size={20} className="text-primary flex-shrink-0" />
                <div>
                  <span className="font-bold text-stone-200 block">Contato do Encarregado de Dados (DPO):</span>
                  Para exercer seus direitos de privacidade ou esclarecer dúvidas, envie um e-mail para{' '}
                  <a href="mailto:contato@literafan.com" className="text-primary hover:underline font-medium">contato@literafan.com</a>.
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="p-4 bg-primary/10 border border-primary/20 rounded-2xl text-xs text-stone-200">
                <p className="font-semibold text-primary mb-1">Bem-vindo à Comunidade Literafan</p>
                Estes Termos de Uso regulam o acesso, a navegação e a participação no site Literafan. Ao utilizar o site e participar de nossas publicações, você declara estar de acordo com as regras aqui dispostas.
              </div>

              <div>
                <h3 className="font-serif text-lg font-bold text-white mb-2 flex items-center gap-2">
                  <span className="text-primary font-sans text-sm font-bold">1.</span> Inscrição na Newsletter & Acesso Interativo
                </h3>
                <p className="leading-relaxed">
                  Para incentivar uma comunidade autêntica, humana e protegida contra spam automatizado, o acesso às ferramentas de interação — especificamente <strong className="text-white">curtir artigos, curtir fanarts e publicar comentários</strong> — é reservado aos leitores inscritos na nossa Newsletter gratuita ou autenticados na plataforma. A inscrição pode ser realizada a qualquer momento no formulário do rodapé ou através do modal de acesso.
                </p>
              </div>

              <div>
                <h3 className="font-serif text-lg font-bold text-white mb-2 flex items-center gap-2">
                  <span className="text-primary font-sans text-sm font-bold">2.</span> Diretrizes de Conduta e Comentários
                </h3>
                <p className="mb-2">Ao interagir nas seções de comentários de artigos e fanarts, você se compromete a:</p>
                <ul className="list-disc pl-6 space-y-1.5 text-stone-400">
                  <li>Manter um tom respeitoso, construtivo e cordial com autores, artistas e outros membros da comunidade.</li>
                  <li>Não publicar mensagens contendo insultos, difamação, calúnia, assédio ou qualquer forma de discriminação (de raça, gênero, orientação sexual, religião ou crença).</li>
                  <li>Não disseminar spam, links promocionais não solicitados ou publicidade pessoal em caixas de comentários.</li>
                  <li>Não se passar por outra pessoa ou usar nomes/avatares enganosos.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-serif text-lg font-bold text-white mb-2 flex items-center gap-2">
                  <span className="text-primary font-sans text-sm font-bold">3.</span> Propriedade Intelectual e Fanarts
                </h3>
                <p className="mb-2">
                  O Literafan valoriza e respeita a autoria literária e artística:
                </p>
                <ul className="list-disc pl-6 space-y-1.5 text-stone-400">
                  <li><strong className="text-stone-200">Direitos dos Artistas:</strong> Todas as fanarts, ilustrações e textos enviados para a comunidade continuam sob propriedade autoral dos seus respectivos criadores. Ao enviar seu material, o criador autoriza a veiculação gratuita no Literafan com finalidade de divulgação cultural e apreciação pelos leitores.</li>
                  <li><strong className="text-stone-200">Homenagem Literária:</strong> As fanarts baseadas em obras publicadas são tributos criativos desenvolvidos por fãs. O Literafan reconhece os direitos dos criadores originais das franquias literárias citadas.</li>
                  <li><strong className="text-stone-200">Artigos e Textos Editoriais:</strong> Os textos publicados pelos editores do Literafan são protegidos pelas leis de direito autoral, sendo vedada sua reprodução total ou parcial sem menção explícita de fonte e autoria.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-serif text-lg font-bold text-white mb-2 flex items-center gap-2">
                  <span className="text-primary font-sans text-sm font-bold">4.</span> Moderação e Suspensão de Acesso
                </h3>
                <p>
                  O Literafan reserva-se o direito soberano de remover, a qualquer momento e sem aviso prévio, comentários ou publicações que violem estes termos ou as leis vigentes. Perfis que insistirem em condutas inadequadas poderão ter sua inscrição e permissões de interação permanentemente revogadas.
                </p>
              </div>

              <div>
                <h3 className="font-serif text-lg font-bold text-white mb-2 flex items-center gap-2">
                  <span className="text-primary font-sans text-sm font-bold">5.</span> Isenção de Responsabilidade
                </h3>
                <p>
                  As opiniões expressas nos comentários de leitores e nos textos submetidos pela comunidade pertencem exclusivamente aos seus respectivos autores, não refletindo necessariamente a posição editorial do Literafan.
                </p>
              </div>

              <div className="p-4 bg-stone-900/60 border border-stone-800 rounded-2xl flex items-center gap-3 text-xs text-stone-400">
                <Mail size={20} className="text-primary flex-shrink-0" />
                <div>
                  <span className="font-bold text-stone-200 block">Dúvidas ou denúncias de conteúdo:</span>
                  Entre em contato com a equipe editorial pelo e-mail{' '}
                  <a href="mailto:contato@literafan.com" className="text-primary hover:underline font-medium">contato@literafan.com</a>.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="p-4 md:px-8 border-t border-stone-800 bg-[#0a0a0a] flex items-center justify-between">
          <p className="text-xs text-stone-500 font-sans">
            Ao utilizar a plataforma, você aceita estas condições de uso.
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-stone-800 hover:bg-stone-700 text-stone-200 font-sans font-bold text-xs rounded-full transition-colors"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalModal;
